/*
  价格计划弹框
*/
import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useImperativeHandle,
  useContext,
} from 'react';
import { Modal, Button, Form, Input, InputNumber, Row, Col } from 'antd';
import cls from 'classnames';
import { renderIntlLabel } from '@/utils';
import { fetchCommissionRate } from '@/api/bill/index';
import { ALL_PAYMENT_TYPE_LIST } from '@/config/constant';
import { getMoneyRulesItem } from '@/components/InputMoney/validator';
import { getPortal } from '@/components/PortalContext';
import IntlContext from '@/components/Intl/IntlContext';
import { parse2Number, parseFen2Currency, parseFen2Yuan } from '@/utils/number';

import { IConfig, IProps } from './interface';
import { priceReg } from './help';
import styles from './index.module.scss';

const formItemLayout = {
  labelCol: { span: 6 },
  wrapperCol: { span: 18 },
};

// modal wrapper
const ModalWrapper: React.FC<IProps> = (props) => {
  const {
    innerRef,
    title,
    closable,
    className,
    cancelText,
    confirmText,
    onCancel,
    onConfirm,
    onClose,
    channelNo, // 渠道订单号
    payChanel, // 支付渠道
    maxRefoundAmount, // 最大可退金额
  } = props;

  const [visible, setVisible] = useState(false);
  const [rate, setRate] = useState(0);

  const [form] = Form.useForm();
  const intlCtx = useContext(IntlContext);

  useImperativeHandle(
    innerRef,
    () => ({
      closeModal: () => setVisible(false),
    }),
    [],
  );

  // 退款入账-校验输入的退款金额 - 顾客预计实收 * (1-佣金费率)=退款入账金额
  const checkAmount = (e: any) => {
    const val = e.target.value;
    const maxAmount = parse2Number(parseFen2Yuan(maxRefoundAmount));

    // 顾客实收
    let actualAmount = Math.round((val / (1 - rate / 100)) * 100);

    if (priceReg.test(val) && val > maxAmount) {
      actualAmount = Math.round((maxAmount / (1 - rate / 100)) * 100);
      form.setFieldsValue({
        refundAmount: parseFen2Yuan(maxRefoundAmount),
        actualAmount: parseFen2Yuan(actualAmount),
      });
    } else if (Number(val) < 0) {
      form.setFieldsValue({
        refundAmount: '0.00',
        actualAmount: '0.00',
      });
    } else {
      form.setFieldsValue({
        refundAmount: parseFen2Yuan(Math.round(val * 100)),
        actualAmount: parseFen2Yuan(actualAmount),
      });
    }
  };

  // 顾客实收-简单校验输入的退款金额 - 顾客预计实收 * (1-佣金费率)=退款入账金额
  const checkActualAmount = (e: any) => {
    const val = e.target.value;

    // 最大值：最大可退金额/(1-佣金费率)
    const maxAmount = parse2Number(parseFen2Yuan(maxRefoundAmount));
    const max = Math.round((maxAmount / (1 - rate / 100)) * 100) / 100;

    if (priceReg.test(val) && Number(val) > max) {
      form.setFieldsValue({
        actualAmount: parseFen2Yuan(max * 100),
        refundAmount: parseFen2Yuan(maxRefoundAmount),
      });
    } else if (Number(val) < 0) {
      form.setFieldsValue({
        actualAmount: '0.00',
        refundAmount: '0.00',
      });
    } else {
      form.setFieldsValue({
        actualAmount: parseFen2Yuan(val * 100),
        refundAmount: parseFen2Yuan(Math.round(val * (1 - rate / 100) * 100)),
      });
    }
  };

  // 佣金费率 顾客实收-简单校验输入的退款金额 - 顾客预计实收 * (1-佣金费率)=退款入账金额
  const checkRate = (e: any) => {
    const val = Number(e.target.value);

    const values = form.getFieldsValue(['actualAmount', 'refundAmount']);
    if (val > 100 || val === 100) {
      setRate(100);
      form.setFieldsValue({
        actualAmount: '0.00',
      });
    } else if (val < 0) {
      setRate(0);
      form.setFieldsValue({
        actualAmount: values.refundAmount,
      });
    } else {
      setRate(val);
      const actualAmount = Math.round((values.refundAmount / (1 - val / 100)) * 100);
      form.setFieldsValue({
        actualAmount: parseFen2Yuan(actualAmount),
      });
    }
  };

  // 获取佣金率 - 顾客预计实收 * (1-佣金费率)=退款入账金额
  const getRate = async () => {
    try {
      const { code, data } = await fetchCommissionRate({ channelNo, paymentType: payChanel });
      if (code === 0 && data) {
        const commissionRate = data.commissionRate || 0;
        setRate(commissionRate * 100);
        const maxAmount = parse2Number(parseFen2Yuan(maxRefoundAmount));
        const actualAmount = Math.round((maxAmount / (1 - commissionRate)) * 100);
        form.setFieldsValue({
          actualAmount: parseFen2Yuan(actualAmount),
        });
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getRate();
    setVisible(true);
  }, []);

  // 支付渠道文本匹配
  const payChanelItem = ALL_PAYMENT_TYPE_LIST.filter((item) => item.value === payChanel);
  const payChanelText = payChanelItem.length > 0 ? payChanelItem[0].label : '--';

  const showCloseIcon = closable ?? !!title;

  return (
    <Modal
      wrapClassName={cls(styles.RefoundCalculatorModal, className)}
      destroyOnClose
      centered
      maskClosable={false}
      afterClose={onClose}
      visible={visible}
      width={560}
      title={title}
      onCancel={onCancel}
      closable={showCloseIcon}
      focusTriggerAfterClose={false}
      footer={[
        cancelText ? (
          <Button onClick={onCancel} key="cancel">
            {cancelText}
          </Button>
        ) : null,
        confirmText ? (
          <Button type="primary" key="confirm">
            {confirmText}
          </Button>
        ) : null,
      ]}
    >
      <Form form={form} {...formItemLayout}>
        <Form.Item label={renderIntlLabel('order_frontDesk_label_paymentType')}>
          {payChanelText}
        </Form.Item>
        <Form.Item label={renderIntlLabel('order_frontDesk_label_commission_rate')}>
          <InputNumber
            controls={false}
            min={0}
            max={100}
            value={rate}
            onPressEnter={checkRate}
            onBlur={checkRate}
          />
          %
        </Form.Item>
        <Form.Item label={renderIntlLabel('order_frontDesk_label_maximum_refundable_amount')}>
          {parseFen2Currency(maxRefoundAmount)}
        </Form.Item>
        <Form.Item label={renderIntlLabel('order_frontDesk_label_Refund_the_amount_credited')}>
          <Row gutter={8}>
            <Col span={12}>
              <Form.Item
                name="refundAmount"
                initialValue={parseFen2Yuan(maxRefoundAmount)}
                rules={[
                  {
                    required: true,
                    message: renderIntlLabel('order_frontDesk_validate_enter_the_refund_amount'),
                  },
                  getMoneyRulesItem(),
                ]}
              >
                <Input
                  allowClear
                  prefix={intlCtx.TenantBaseConfig.CurrencySymbol}
                  placeholder={renderIntlLabel('order_frontDesk_validate_enter_the_refund_amount')}
                  onPressEnter={checkAmount}
                  onBlur={checkAmount}
                />
              </Form.Item>
            </Col>
            <Col span={3}>
              <Button
                type="primary"
                onClick={() => {
                  const value = form.getFieldValue(['refundAmount']);
                  onConfirm(value);
                }}
              >
                {renderIntlLabel('order_action_fill_in_pre_page')}
              </Button>
            </Col>
          </Row>
        </Form.Item>
        <Form.Item label={renderIntlLabel('order_frontDesk_label_expected_receive')}>
          <Row gutter={8}>
            <Col span={12}>
              <Form.Item
                name="actualAmount"
                rules={[
                  {
                    required: true,
                    message: renderIntlLabel('order_frontDesk_validate_input_expected_receive'),
                  },
                  getMoneyRulesItem(),
                ]}
              >
                <Input
                  allowClear
                  prefix={intlCtx.TenantBaseConfig.CurrencySymbol}
                  placeholder={renderIntlLabel('order_frontDesk_validate_input_expected_receive')}
                  onPressEnter={checkActualAmount}
                  onBlur={checkActualAmount}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form.Item>
        <div className={styles.infos}>
          {renderIntlLabel('guest_title_auxiliary_calc')} <br />
          {renderIntlLabel('guest_title_ctrip_mt_douyin_communication')}
        </div>
      </Form>
    </Modal>
  );
};

// modal portal
const usePortalModal = () => {
  const modalRef = useRef();
  const modal = useMemo(() => {
    let nodeId: string;

    const closeDrawer = () => {
      if (nodeId) {
        getPortal().removePortalNode(nodeId);
      }
    };

    const showDrawer: (config: IConfig) => void = (config) => {
      nodeId = getPortal().renderPortalNode(() => (
        <ModalWrapper {...config} innerRef={modalRef} onClose={closeDrawer} />
      ));
    };

    return {
      showDrawer: (config: IConfig) => {
        modalRef.current = null;
        closeDrawer();
        showDrawer(config);
      },
      closeDrawer: () => {
        const modalInst = modalRef.current;
        if (modalInst) {
          (modalInst as any).closeModal();
        }
      },
    };
  }, []);

  useEffect(() => {
    return () => {
      modal.closeDrawer();
    };
  }, [modal]);

  return modal;
};

export default usePortalModal;

export type PortalInstance = ReturnType<typeof usePortalModal>;
